import React, { useEffect, useState, useRef } from 'react';
import { X, Image as ImageIcon, Send, User, Users, Search, Building } from 'lucide-react';
// Define LGS subjects for cascading selection
const lgsSubjects = [{
  id: 'turkce',
  title: 'Türkçe',
  topics: ['Sözcükte Anlam', 'Deyimler ve Atasözleri', 'Cümlede Anlam', 'Sözel Mantık / Görsel Okuma', 'Parçada Anlam', 'Söz Sanatları', 'Metin Türleri', 'Noktalama İşaretleri', 'Yazım Kuralları', 'Fiilimsiler', 'Cümlenin Öğeleri', 'Cümle Türleri', 'Anlatım Bozukluğu']
}, {
  id: 'matematik',
  title: 'Matematik',
  topics: ['Çarpanlar ve Katlar', 'Üslü Sayılar', 'Köklü Sayılar', 'Veri Analizi', 'Olasılık', 'Cebirsel İfadeler', 'Doğrusal Denklemler ve Eğim', 'Eşitsizlikler', 'Üçgenler', 'Eşlik ve Benzerlik', 'Dönüşüm Geometrisi', 'Geometrik Cisimler']
}, {
  id: 'fen',
  title: 'Fen Bilimleri',
  topics: ['Mevsimler ve İklim', 'DNA ve Genetik Kod', 'Basınç', 'Madde ve Endüstri', 'Basit Makineler', 'Enerji Dönüşümleri', 'Elektrik Yükleri']
}, {
  id: 'inkilap',
  title: 'T.C. İnkılap Tarihi',
  topics: ['Bir Kahraman Doğuyor', 'Milli Uyanış', 'Milli Bir Destan', 'Atatürkçülük', 'Demokratikleşme Çabaları', 'Atatürk Dönemi Dış Politika']
}, {
  id: 'ingilizce',
  title: 'İngilizce',
  topics: ['Friendship', 'Teen Life', 'In The Kitchen', 'On The Phone', 'The Internet', 'Adventures', 'Tourism', 'Chores', 'Science', 'Natural Forces']
}, {
  id: 'din',
  title: 'Din Kültürü',
  topics: ['Kader İnancı', 'Hz. Musa', 'Zekat ve Sadaka', 'Din ve Hayat', 'Hz. Muhammed', "Kur'an-ı Kerim", 'Paylaşma ve Yardımlaşma']
}];
interface NewForumTopicProps {
  onClose: () => void;
  onSubmit: (topic: {
    title: string;
    category: string;
    topic: string;
    content: string;
    image: File | null;
    visibility: 'public' | 'institution' | 'private';
    selectedUsers: string[];
  }) => void;
}
export function NewForumTopic({
  onClose,
  onSubmit
}: NewForumTopicProps) {
  // Form state with proper defaults
  const [form, setForm] = useState({
    title: '',
    category: '',
    topic: '',
    content: '',
    image: null as File | null,
    visibility: 'public' as 'public' | 'institution' | 'private'
  });
  // Available topics based on selected category
  const [availableTopics, setAvailableTopics] = useState<string[]>([]);
  // Update available topics when category changes
  useEffect(() => {
    if (form.category) {
      const selectedSubject = lgsSubjects.find(subject => subject.title === form.category);
      if (selectedSubject) {
        setAvailableTopics(selectedSubject.topics);
        // Reset topic if changing category
        setForm(prev => ({
          ...prev,
          topic: ''
        }));
      } else {
        setAvailableTopics([]);
      }
    } else {
      setAvailableTopics([]);
    }
  }, [form.category]);
  // Check if form is valid for submission
  const isFormValid = () => {
    if (!form.title || !form.content) {
      return false;
    }
    // If active tab is 'question', require category, and topic unless category is "Diğer"
    if (activeTab === 'question') {
      if (!form.category) {
        return false;
      }
      // Only require topic if category is not "Diğer"
      if (form.category !== 'Diğer' && !form.topic) {
        return false;
      }
    }
    if (form.visibility === 'private' && selectedTeachers.length === 0) {
      return false;
    }
    return true;
  };
  // Add activeTab prop to track if it's a question or other type
  const [activeTab, setActiveTab] = useState<'question' | 'other'>('question');
  // Teacher selection state
  const [selectedTeachers, setSelectedTeachers] = useState<string[]>([]);
  const [searchTeacher, setSearchTeacher] = useState('');
  const [showTeacherDropdown, setShowTeacherDropdown] = useState(false);
  // Image handling state
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  // Available teachers for selection
  const availableTeachers = [{
    id: '1',
    name: 'Ahmet Yılmaz',
    subject: 'Matematik'
  }, {
    id: '2',
    name: 'Zeynep Kaya',
    subject: 'Türkçe'
  }, {
    id: '3',
    name: 'Mehmet Demir',
    subject: 'Fen Bilimleri'
  }, {
    id: '4',
    name: 'Ayşe Şahin',
    subject: 'İngilizce'
  }, {
    id: '5',
    name: 'Ali Öztürk',
    subject: 'Tarih'
  }, {
    id: '6',
    name: 'Fatma Çelik',
    subject: 'Coğrafya'
  }, {
    id: '7',
    name: 'Mustafa Aydın',
    subject: 'Fizik'
  }, {
    id: '8',
    name: 'Hatice Yıldız',
    subject: 'Kimya'
  }];
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowTeacherDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Filter teachers based on search term
  const filteredTeachers = availableTeachers.filter(teacher => (teacher.name.toLowerCase().includes(searchTeacher.toLowerCase()) || teacher.subject.toLowerCase().includes(searchTeacher.toLowerCase())) && !selectedTeachers.includes(teacher.id));
  const handleRemoveTeacher = (teacherId: string) => {
    setSelectedTeachers(selectedTeachers.filter(id => id !== teacherId));
  };
  // Image handling functions
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setForm({
        ...form,
        image: file
      });
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  const handleImageClick = () => {
    fileInputRef.current?.click();
  };
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.add('border-purple-500');
  };
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('border-purple-500');
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('border-purple-500');
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      setForm({
        ...form,
        image: file
      });
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  const handleRemoveImage = () => {
    setForm({
      ...form,
      image: null
    });
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  // Form submission
  const handleSubmit = () => {
    onSubmit({
      ...form,
      selectedUsers: selectedTeachers,
      image: form.image
    });
  };
  return <div className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-50 overflow-y-auto" data-id="element-604">
      <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600 w-full max-w-3xl my-8" data-id="element-605">
        <div className="relative bg-black/90 rounded-xl" data-id="element-606">
          <div className="p-6 border-b border-gray-800" data-id="element-607">
            <div className="flex justify-between items-center" data-id="element-608">
              <h2 className="text-2xl font-bold" data-id="element-609">Yeni Soru</h2>
              <button onClick={onClose} className="text-gray-400 hover:text-white" data-id="element-610">
                <X className="w-6 h-6" data-id="element-611" />
              </button>
            </div>
          </div>
          <div className="p-6 space-y-6" data-id="element-612">
            {/* Tab Selection */}
            <div className="border-b border-gray-800" data-id="element-613">
              <div className="flex" data-id="element-614">
                <button onClick={() => setActiveTab('question')} className={`px-6 py-3 font-medium text-sm relative ${activeTab === 'question' ? 'text-purple-400' : 'text-gray-400 hover:text-gray-300'}`} data-id="element-615">
                  Soru Çözüm
                  {activeTab === 'question' && <span className="absolute bottom-0 left-0 w-full h-0.5 bg-purple-500" data-id="element-616"></span>}
                </button>
                <button onClick={() => setActiveTab('other')} className={`px-6 py-3 font-medium text-sm relative ${activeTab === 'other' ? 'text-purple-400' : 'text-gray-400 hover:text-gray-300'}`} data-id="element-617">
                  Diğer
                  {activeTab === 'other' && <span className="absolute bottom-0 left-0 w-full h-0.5 bg-purple-500" data-id="element-618"></span>}
                </button>
              </div>
            </div>

            {/* Title */}
            <div className="space-y-2" data-id="element-619">
              <label className="text-sm font-medium text-gray-300" data-id="element-620">
                Başlık
              </label>
              <input type="text" value={form.title} onChange={e => setForm({
              ...form,
              title: e.target.value
            })} className="w-full bg-white/5 text-white rounded-lg px-4 py-3 focus:ring-2 ring-purple-500/20 border border-white/10 focus:border-purple-500/50" placeholder="Soru başlığını girin..." data-id="element-621" />
            </div>

            {/* Cascading Subject and Topic Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-622">
              {/* Subject Selection */}
              <div className="space-y-2" data-id="element-623">
                <label className="text-sm font-medium text-gray-300" data-id="element-624">
                  Ders Seç
                </label>
                <select value={form.category} onChange={e => setForm({
                ...form,
                category: e.target.value
              })} className="w-full bg-black/50 text-white rounded-lg px-4 py-3 focus:ring-2 ring-purple-500/20 border border-white/10 focus:border-purple-500/50" data-id="element-625">
                  <option value="" data-id="element-626">Ders Seçiniz</option>
                  {activeTab === 'other' && <option value="Diğer" data-id="element-627">Diğer</option>}
                  {lgsSubjects.map(subject => <option key={subject.id} value={subject.title} data-id="element-628">
                      {subject.title}
                    </option>)}
                </select>
              </div>

              {/* Topic Selection - Only enabled when subject is selected and not "Diğer" */}
              <div className="space-y-2" data-id="element-629">
                <label className="text-sm font-medium text-gray-300" data-id="element-630">
                  Konu Seç
                </label>
                <select value={form.topic} onChange={e => setForm({
                ...form,
                topic: e.target.value
              })} disabled={!form.category || form.category === 'Diğer'} className={`w-full bg-black/50 text-white rounded-lg px-4 py-3 focus:ring-2 ring-purple-500/20 border border-white/10 ${form.category && form.category !== 'Diğer' ? 'focus:border-purple-500/50' : 'opacity-50 cursor-not-allowed'}`} data-id="element-631">
                  <option value="" data-id="element-632">Konu Seçiniz</option>
                  {availableTopics.map(topic => <option key={topic} value={topic} data-id="element-633">
                      {topic}
                    </option>)}
                </select>
              </div>
            </div>

            {/* Content */}
            <div className="space-y-2" data-id="element-634">
              <label className="text-sm font-medium text-gray-300" data-id="element-635">
                İçerik
              </label>
              <div className="relative" data-id="element-636">
                <textarea value={form.content} onChange={e => setForm({
                ...form,
                content: e.target.value
              })} className="w-full bg-white/5 text-white rounded-lg px-4 py-3 min-h-[150px] focus:ring-2 ring-purple-500/20 border border-white/10 focus:border-purple-500/50 resize-none" placeholder="Sorunuzu detaylı bir şekilde açıklayın..." data-id="element-637" />
              </div>
            </div>

            {/* Image Upload with Drag & Drop */}
            <div className="space-y-2" data-id="element-638">
              <label className="text-sm font-medium text-gray-300" data-id="element-639">
                Görsel Ekle (İsteğe Bağlı)
              </label>
              <input type="file" ref={fileInputRef} onChange={handleImageChange} accept="image/*" className="hidden" data-id="element-640" />
              {!imagePreview ? <div onClick={handleImageClick} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} className="border-2 border-dashed border-purple-500/50 rounded-lg p-8 text-center cursor-pointer hover:bg-white/5 transition-colors" data-id="element-641">
                  <div className="flex flex-col items-center gap-2" data-id="element-642">
                    <ImageIcon className="w-10 h-10 text-purple-400" data-id="element-643" />
                    <p className="text-purple-400" data-id="element-644">
                      Görseli buraya sürükle veya tıklayarak seç
                    </p>
                    <p className="text-xs text-gray-400 mt-2" data-id="element-645">
                      PNG, JPG veya GIF formatında, max 5MB
                    </p>
                  </div>
                </div> : <div className="relative mt-2 rounded-lg overflow-hidden border border-gray-700" data-id="element-646">
                  <img src={imagePreview} alt="Preview" className="w-full max-h-60 object-contain bg-black/30" data-id="element-647" />
                  <button type="button" onClick={handleRemoveImage} className="absolute top-2 right-2 p-1.5 bg-red-500/80 text-white rounded-full hover:bg-red-600 transition-colors" data-id="element-648">
                    <X className="w-4 h-4" data-id="element-649" />
                  </button>
                </div>}
            </div>

            {/* Visibility Options with Radio Buttons */}
            <div className="space-y-3" data-id="element-650">
              <label className="text-sm font-medium text-gray-300" data-id="element-651">
                Görünürlük
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-652">
                {/* Public Option */}
                <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${form.visibility === 'public' ? 'bg-purple-500/20 border border-purple-500/50' : 'bg-white/5 border border-white/10 hover:bg-white/10'}`} data-id="element-653">
                  <div className="relative flex items-center justify-center" data-id="element-654">
                    <input type="radio" name="visibility" checked={form.visibility === 'public'} onChange={() => setForm({
                    ...form,
                    visibility: 'public'
                  })} className="sr-only" data-id="element-655" />
                    <div className={`w-5 h-5 rounded-full border ${form.visibility === 'public' ? 'border-purple-500' : 'border-gray-400'}`} data-id="element-656"></div>
                    {form.visibility === 'public' && <div className="absolute w-3 h-3 rounded-full bg-purple-500" data-id="element-657"></div>}
                  </div>
                  <div className="flex items-center gap-2" data-id="element-658">
                    <Users className={`w-5 h-5 ${form.visibility === 'public' ? 'text-purple-400' : 'text-gray-400'}`} data-id="element-659" />
                    <span className={form.visibility === 'public' ? 'text-purple-400' : 'text-gray-300'} data-id="element-660">
                      Herkese Açık
                    </span>
                  </div>
                </label>

                {/* Institution Option */}
                <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${form.visibility === 'institution' ? 'bg-purple-500/20 border border-purple-500/50' : 'bg-white/5 border border-white/10 hover:bg-white/10'}`} data-id="element-661">
                  <div className="relative flex items-center justify-center" data-id="element-662">
                    <input type="radio" name="visibility" checked={form.visibility === 'institution'} onChange={() => setForm({
                    ...form,
                    visibility: 'institution'
                  })} className="sr-only" data-id="element-663" />
                    <div className={`w-5 h-5 rounded-full border ${form.visibility === 'institution' ? 'border-purple-500' : 'border-gray-400'}`} data-id="element-664"></div>
                    {form.visibility === 'institution' && <div className="absolute w-3 h-3 rounded-full bg-purple-500" data-id="element-665"></div>}
                  </div>
                  <div className="flex items-center gap-2" data-id="element-666">
                    <Building className={`w-5 h-5 ${form.visibility === 'institution' ? 'text-purple-400' : 'text-gray-400'}`} data-id="element-667" />
                    <span className={form.visibility === 'institution' ? 'text-purple-400' : 'text-gray-300'} data-id="element-668">
                      Kurumum
                    </span>
                  </div>
                </label>

                {/* Private Option */}
                <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${form.visibility === 'private' ? 'bg-purple-500/20 border border-purple-500/50' : 'bg-white/5 border border-white/10 hover:bg-white/10'}`} data-id="element-669">
                  <div className="relative flex items-center justify-center" data-id="element-670">
                    <input type="radio" name="visibility" checked={form.visibility === 'private'} onChange={() => setForm({
                    ...form,
                    visibility: 'private'
                  })} className="sr-only" data-id="element-671" />
                    <div className={`w-5 h-5 rounded-full border ${form.visibility === 'private' ? 'border-purple-500' : 'border-gray-400'}`} data-id="element-672"></div>
                    {form.visibility === 'private' && <div className="absolute w-3 h-3 rounded-full bg-purple-500" data-id="element-673"></div>}
                  </div>
                  <div className="flex items-center gap-2" data-id="element-674">
                    <User className={`w-5 h-5 ${form.visibility === 'private' ? 'text-purple-400' : 'text-gray-400'}`} data-id="element-675" />
                    <span className={form.visibility === 'private' ? 'text-purple-400' : 'text-gray-300'} data-id="element-676">
                      Özel
                    </span>
                  </div>
                </label>
              </div>
            </div>

            {/* Teacher Selection for Private Visibility */}
            {form.visibility === 'private' && <div className="space-y-2" data-id="element-677">
                <label className="text-sm font-medium text-gray-300" data-id="element-678">
                  Öğretmen Seç
                </label>
                <div className="space-y-3" ref={searchRef} data-id="element-679">
                  <div className="relative" data-id="element-680">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-681" />
                    <input type="text" value={searchTeacher} onChange={e => {
                  setSearchTeacher(e.target.value);
                  if (e.target.value) {
                    setShowTeacherDropdown(true);
                  }
                }} onFocus={() => {
                  if (searchTeacher) {
                    setShowTeacherDropdown(true);
                  }
                }} className="w-full bg-white/5 text-white rounded-lg pl-10 pr-4 py-3 focus:ring-2 ring-purple-500/20 border border-white/10 focus:border-purple-500/50" placeholder="Öğretmen adı veya branşı..." data-id="element-682" />
                  </div>

                  {/* Selected Teachers */}
                  {selectedTeachers.length > 0 && <div className="flex flex-wrap gap-2 mt-2" data-id="element-683">
                      {selectedTeachers.map(teacherId => {
                  const teacher = availableTeachers.find(t => t.id === teacherId);
                  if (!teacher) return null;
                  return <span key={teacherId} className="px-3 py-1.5 rounded-full bg-purple-500/20 text-purple-400 text-sm flex items-center gap-1" data-id="element-684">
                            {teacher.name} ({teacher.subject})
                            <button onClick={() => handleRemoveTeacher(teacherId)} className="ml-1 hover:text-white" data-id="element-685">
                              <X className="w-4 h-4" data-id="element-686" />
                            </button>
                          </span>;
                })}
                    </div>}

                  {/* Teacher Dropdown - Instantly filters as user types */}
                  {showTeacherDropdown && searchTeacher && <div className="absolute z-10 mt-1 w-full bg-black/90 rounded-lg shadow-lg border border-gray-800 max-h-60 overflow-y-auto" data-id="element-687">
                      {filteredTeachers.length > 0 ? filteredTeachers.map(teacher => <button key={teacher.id} onClick={() => {
                  setSelectedTeachers([...selectedTeachers, teacher.id]);
                  setSearchTeacher('');
                  setShowTeacherDropdown(false);
                }} className="w-full px-4 py-2 text-left hover:bg-purple-500/20 text-white flex items-center gap-2" data-id="element-688">
                            <User className="w-4 h-4 text-gray-400" data-id="element-689" />
                            <div data-id="element-690">
                              <div className="font-medium" data-id="element-691">{teacher.name}</div>
                              <div className="text-xs text-gray-400" data-id="element-692">
                                {teacher.subject}
                              </div>
                            </div>
                          </button>) : <div className="px-4 py-3 text-gray-400 text-center" data-id="element-693">
                          Sonuç bulunamadı
                        </div>}
                    </div>}
                </div>
              </div>}
          </div>

          {/* Footer with Submit Button */}
          <div className="p-6 border-t border-gray-800 flex justify-end gap-3" data-id="element-694">
            <button onClick={onClose} className="px-5 py-2.5 bg-white/5 hover:bg-white/10 text-white rounded-lg transition-colors" data-id="element-695">
              İptal
            </button>
            <button onClick={() => handleSubmit()} disabled={!isFormValid()} className={`px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg transition-colors flex items-center gap-2 ${!isFormValid() ? 'opacity-50 cursor-not-allowed' : 'hover:from-purple-700 hover:to-indigo-700'}`} data-id="element-696">
              <Send className="w-4 h-4" data-id="element-697" />
              Gönder
            </button>
          </div>
        </div>
      </div>
    </div>;
}